/**
 * Walking a cursor-paged list to its end, once, for everything that wants all
 * of it.
 *
 * Four sources had written this by hand or, more often, not at all: pipeline
 * definitions, grouping rules, alert trigger rules and alert destinations each
 * issued ONE un-paginated GET and dropped the `next_cursor` the route hands
 * back. Every service defaults its page to fifty.
 *
 * These are pickers and management surfaces, not feeds, and on those a missing
 * row does not read as "there are more" — it reads as "that one does not
 * exist". Two of the consequences are worse than a short list:
 *
 * - The alert sources rebuild a `knownRuleIds` / `knownDestinationIds` set from
 *   whatever the list returned, and use it to choose POST (create) versus PATCH
 *   (update). A rule past the first page is absent from that set, so editing it
 *   posts a DUPLICATE — and two enabled trigger rules on one condition send the
 *   alert twice.
 * - The grouping-rules page derives which rule the engine applies from the list
 *   it was handed, so a higher-ranked rule on page two meant the page named the
 *   wrong rule as live.
 *
 * Asking for a bigger limit does not fix it either: several of these routes
 * filter for visibility, or for tenancy, AFTER taking the page — so a page
 * legitimately comes back short, or empty, with more behind it. Emptiness is
 * not the stop condition. A missing cursor is.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";

import { isRecord, rowsFromWire } from "./wire";

/** Rows per request. Services clamp their own maximum; this is the ask. */
export const PAGE_SIZE = 100;

/**
 * How many pages one walk will follow.
 *
 * A bound so a route that never stops handing back a cursor cannot hang the
 * surface waiting on it. At {@link PAGE_SIZE} it is far past any tenant we can
 * currently produce on these resources — all of which are authored by hand.
 */
export const MAX_PAGES = 20;

export interface WalkPagesOptions {
  /** Extra query params sent with every page (filters, scope widening). */
  query?: Record<string, string>;
  signal?: AbortSignal;
  pageSize?: number;
  maxPages?: number;
}

/**
 * Every row of a `{items, next_cursor}` list, following the cursor to the end.
 *
 * Returns the rows uncoerced; callers map them through their own `…FromWire`.
 */
export async function walkPages(
  client: ApiClient,
  path: string,
  { query, signal, pageSize = PAGE_SIZE, maxPages = MAX_PAGES }: WalkPagesOptions = {},
): Promise<Record<string, unknown>[]> {
  const rows: Record<string, unknown>[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < maxPages; page += 1) {
    const params: Record<string, string> = {
      ...(query ?? {}),
      limit: String(pageSize),
      ...(cursor ? { cursor } : {}),
    };
    const payload = unwrapData(await client.get<unknown>(path, { query: params, signal }));
    rows.push(...rowsFromWire(payload));

    const next = isRecord(payload) ? payload.next_cursor : undefined;
    cursor = typeof next === "string" && next ? next : undefined;
    if (!cursor) break;
  }
  return rows;
}
